import { useMutation, useQuery } from '@apollo/client';
import {
    faArrowUpRightFromSquare,
    faBolt,
    faChevronRight,
    faFileLines,
    faInbox,
    faPlug,
    faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { formatDistanceToNowStrict } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import AddAssistantMailboxModal from './AddAssistantMailboxModal.tsx';
import RunStatusBadge, { type StatusTone } from '../../../../chat/components/automations/RunStatusBadge.tsx';
import Button from '../../../../core/components/Button.tsx';
import ConfirmModal from '../../../../core/components/ConfirmModal.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { SettingsCard } from '../../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { url } from '../../../../core/utils/link.ts';
import { getDateLocalizations } from '../../../../core/utils/localization.ts';
import FieldsConfig from '../../../../document/components/FieldsConfig.tsx';
import { canSeeMailboxes } from '../../../../users/utils.ts';
import { OVERVIEW_PATH as MAILBOXES_PATH } from '../../../mailboxes/constants.ts';
import {
    ASSISTANT_PATH,
    ASSISTANT_RECORD_PATH,
    OUTLOOK_ADD_IN_URL,
    OVERVIEW_PATH as ASSISTANTS_PATH,
} from '../../constants.ts';
import { DELETE_AGENT, GET_AGENTS, GET_AGENT_PROCESSING_RECORDS, SET_AGENT_ENABLED } from '../../queries.ts';
import {
    getIntegrationKeys,
    getKnowledgeKeys,
    getOverviewNextStep,
    OverviewNextStep,
} from '../../utils/overviewState.ts';
import { formatSkillKey, formatToolProviderKey } from '../config/AgentCardContent.tsx';
import { parseJsonSafe } from '../records/ProcessingRecordModal.tsx';

const TeamIcon = withIcon(faUserGroup);
const SkillsIcon = withIcon(faBolt);
const MailboxIcon = withIcon(faInbox);
const IntegrationsIcon = withIcon(faPlug);
const KnowledgeIcon = withIcon(faFileLines);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);
const ChevronIcon = withIcon(faChevronRight);

const RECENT_ACTIVITY_LIMIT = 5;
const VISIBLE_MAILBOXES = 3;

const linkClassName =
    'rounded text-sm font-medium text-content-brand-default underline-offset-2 hover:underline outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong';

interface OverviewRowProps {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string;
    badge?: { tone: StatusTone; label: string } | null;
}

const OverviewRow = ({ icon: Icon, label, value, badge }: OverviewRowProps) => (
    <div className="flex items-center gap-3">
        <Icon className="w-5 flex-none text-sm text-content-strong" />
        <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-xs font-medium text-content-strong">{label}</span>
            <span className="text-xs text-content-medium">{value}</span>
        </div>
        {badge && <RunStatusBadge tone={badge.tone}>{badge.label}</RunStatusBadge>}
    </div>
);

/** A missing piece is a warning when its card holds the next step, otherwise a neutral note. */
const missingTone = (isNextStep: boolean): StatusTone => (isNextStep ? 'warning' : 'neutral');

interface AssistantOverviewProps {
    agent: any;
    availableSmartInboxTriggers: { id: string; name: string; agentIds: string[] }[];
    onChange: () => Promise<unknown>;
}

/** The Overview tab of an assistant: read-only cards that summarise its setup, link to the tabs that change it, and
 *  pause, resume or delete it. */
const AssistantOverview = ({ agent, availableSmartInboxTriggers, onChange }: AssistantOverviewProps) => {
    const { user } = useApplicationContext();

    const mailboxes = (agent.smartInboxIntegrations || []).map(
        (integration) => integration.mailInbox || integration.name
    );
    const integrationKeys = getIntegrationKeys(agent);
    const knowledgeKeys = getKnowledgeKeys(agent);

    const nextStep = getOverviewNextStep({
        hasMailbox: mailboxes.length > 0,
        workspaceHasMailbox: availableSmartInboxTriggers.length > 0,
        // the Mailboxes page is where a workspace's first mailbox gets connected
        canSetUpMailbox: canSeeMailboxes(user),
        hasIntegrations: integrationKeys.length > 0,
        hasKnowledge: knowledgeKeys.length > 0,
    });

    return (
        <div className="flex flex-col gap-4">
            <GeneralCard
                agent={agent}
                mailboxes={mailboxes}
                nextStep={nextStep}
                availableSmartInboxTriggers={availableSmartInboxTriggers}
                onChange={onChange}
            />
            <RecentActivityCard agentId={agent.id} />
            <IntegrationsAndKnowledgeCard
                agent={agent}
                integrationKeys={integrationKeys}
                knowledgeKeys={knowledgeKeys}
                nextStep={nextStep}
            />
            <StatusCard agent={agent} onChange={onChange} />
        </div>
    );
};

const GeneralCard = ({
    agent,
    mailboxes,
    nextStep,
    availableSmartInboxTriggers,
    onChange,
}: {
    agent: any;
    mailboxes: string[];
    nextStep: OverviewNextStep | null;
    availableSmartInboxTriggers: AssistantOverviewProps['availableSmartInboxTriggers'];
    onChange: () => Promise<unknown>;
}) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const navigate = useNavigate();
    const [isAddMailboxOpen, setIsAddMailboxOpen] = useState(false);

    const workspaceHasMailbox = availableSmartInboxTriggers.length > 0;
    const skills = (agent.skills || []).map((skill) => formatSkillKey(skill.skillKey, tAgentConfig));

    let mailboxValue: string;
    let mailboxBadge: OverviewRowProps['badge'] = null;
    if (mailboxes.length > 0) {
        const hidden = mailboxes.length - VISIBLE_MAILBOXES;
        mailboxValue = mailboxes.slice(0, VISIBLE_MAILBOXES).join(', ');
        if (hidden > 0) mailboxValue += ` ${t('detail.general.moreMailboxes', { count: hidden })}`;
    } else if (!workspaceHasMailbox) {
        mailboxValue = t('detail.general.noMailboxInWorkspace');
        mailboxBadge = { tone: 'warning', label: t('detail.badges.notConnected') };
    } else {
        mailboxValue = t('detail.general.mailboxNotAssigned');
        mailboxBadge = { tone: missingTone(nextStep === 'addMailbox'), label: t('detail.badges.notAssigned') };
    }

    // Fall A: back to this Overview from the Mailboxes page via its back arrow
    const openMailboxes = () =>
        navigate(url(MAILBOXES_PATH), { state: { fromAssistant: { id: agent.id, name: agent.name } } });

    return (
        <SettingsCard title={t('detail.general.title')} description={t('detail.general.description')}>
            <div className="flex flex-col gap-4">
                <OverviewRow
                    icon={TeamIcon}
                    label={t('detail.general.team')}
                    value={agent.team?.name || t('detail.general.noTeam')}
                />
                <OverviewRow
                    icon={SkillsIcon}
                    label={t('detail.general.skills')}
                    value={skills.length > 0 ? skills.join(', ') : t('detail.general.noSkills')}
                />
                <OverviewRow
                    icon={MailboxIcon}
                    label={t('detail.general.mailboxes')}
                    value={mailboxValue}
                    badge={mailboxBadge}
                />
            </div>

            {mailboxes.length === 0 && (
                <div className="flex flex-wrap items-center gap-2">
                    {workspaceHasMailbox ? (
                        <Button
                            type="button"
                            variant={nextStep === 'addMailbox' ? 'primary' : 'outline'}
                            onClick={() => setIsAddMailboxOpen(true)}
                        >
                            {t('detail.general.addMailbox')}
                        </Button>
                    ) : nextStep === 'setUpMailbox' ? (
                        <Button type="button" variant="primary" onClick={openMailboxes}>
                            {t('detail.general.setUpMailbox')}
                        </Button>
                    ) : (
                        // S0b: only an admin can connect the workspace's first mailbox
                        <>
                            <RunStatusBadge tone="neutral">{t('detail.general.requiresAdmin')}</RunStatusBadge>
                            <span className="text-xs text-content-medium">{t('detail.general.askYourAdmin')}</span>
                        </>
                    )}
                </div>
            )}

            {isAddMailboxOpen && (
                <AddAssistantMailboxModal
                    agent={agent}
                    availableSmartInboxTriggers={availableSmartInboxTriggers}
                    onClose={() => setIsAddMailboxOpen(false)}
                    onAdded={onChange}
                />
            )}
        </SettingsCard>
    );
};

const RecentActivityCard = ({ agentId }: { agentId: string }) => {
    const { t } = useTranslation('assistants');

    const { data, loading } = useQuery(GET_AGENT_PROCESSING_RECORDS, {
        fetchPolicy: 'cache-and-network',
        variables: { agentId, offset: 0, limit: RECENT_ACTIVITY_LIMIT, isTesting: false },
    });
    const records = data?.filteredAgentProcessingRecordsList?.records || [];
    const locale = getDateLocalizations();

    return (
        <SettingsCard
            title={t('detail.activity.title')}
            description={t('detail.activity.description')}
            aria-busy={loading && !data}
        >
            {records.length > 0 ? (
                <>
                    <FieldsConfig.Card>
                        {records.map((record) => {
                            const inputData = parseJsonSafe(record.inputData);

                            return (
                                <Link
                                    key={record.id}
                                    to={url(ASSISTANT_RECORD_PATH, { channelId: agentId, recordId: record.id })}
                                    className="flex items-center gap-4 px-4 py-3 text-sm outline-none transition-colors hover:bg-surface-medium focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-line-brand-strong"
                                >
                                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <span className="truncate font-medium text-content-strong">
                                            {inputData?.subject || t('detail.activity.noSubject')}
                                        </span>
                                        {inputData?.sender && (
                                            <span className="truncate text-xs text-content-medium">
                                                {inputData.sender}
                                            </span>
                                        )}
                                    </span>
                                    <span className="w-28 flex-none whitespace-nowrap text-right text-xs tabular-nums text-content-medium">
                                        {formatDistanceToNowStrict(new Date(record.createdAt), {
                                            addSuffix: true,
                                            locale,
                                        })}
                                    </span>
                                    <ChevronIcon className="flex-none text-xs text-content-medium" />
                                </Link>
                            );
                        })}
                    </FieldsConfig.Card>
                    <Button asChild variant="outline" className="self-start">
                        <Link to={url(ASSISTANT_PATH, { channelId: agentId, tab: 'activity' })}>
                            {t('detail.activity.viewAll')}
                        </Link>
                    </Button>
                </>
            ) : (
                !loading && (
                    <div className="flex flex-col items-start gap-1">
                        <span className="text-xs font-medium text-content-strong">
                            {t('detail.activity.emptyTitle')}
                        </span>
                        <span className="text-xs text-content-medium">{t('detail.activity.emptyDescription')}</span>
                        <a
                            href={OUTLOOK_ADD_IN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classnames(linkClassName, 'mt-2 inline-flex items-center gap-1.5')}
                        >
                            {t('detail.activity.setUpOutlook')} <ExternalLinkIcon />
                        </a>
                    </div>
                )
            )}
        </SettingsCard>
    );
};

const INTEGRATION_LABEL_KEYS = {
    masterData: 'detail.integrations.masterData',
    orderHistory: 'detail.integrations.orderHistory',
    documentCreation: 'detail.integrations.documentCreation',
};

const KNOWLEDGE_LABEL_KEYS = {
    files: 'detail.knowledge.files',
    websites: 'detail.knowledge.websites',
    sharepoint: 'detail.knowledge.sharepoint',
    sftp: 'detail.knowledge.sftp',
    expertKnowledge: 'detail.knowledge.expertKnowledge',
};

const IntegrationsAndKnowledgeCard = ({
    agent,
    integrationKeys,
    knowledgeKeys,
    nextStep,
}: {
    agent: any;
    integrationKeys: string[];
    knowledgeKeys: string[];
    nextStep: OverviewNextStep | null;
}) => {
    const { t } = useTranslation('assistants');
    const { t: tAgentConfig } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const hasIntegrations = integrationKeys.length > 0;
    const hasKnowledge = knowledgeKeys.length > 0;
    // the card holds the next step when it is one of its own buttons
    const holdsNextStep = nextStep === 'addKnowledge' || nextStep === 'addIntegration';

    const integrations = integrationKeys.map((key) =>
        key === 'erp' ? formatToolProviderKey(agent.erpToolProvider.key, tAgentConfig) : t(INTEGRATION_LABEL_KEYS[key])
    );
    const knowledge = knowledgeKeys.map((key) => t(KNOWLEDGE_LABEL_KEYS[key]));

    const tabUrl = (tab: string) => url(ASSISTANT_PATH, { channelId: agent.id, tab });

    return (
        <SettingsCard
            title={t('detail.integrationsAndKnowledge.title')}
            description={t('detail.integrationsAndKnowledge.description')}
        >
            <div className="flex flex-col gap-4">
                <OverviewRow
                    icon={IntegrationsIcon}
                    label={t('detail.integrations.label')}
                    value={hasIntegrations ? integrations.join(', ') : t('detail.integrations.none')}
                    badge={
                        hasIntegrations
                            ? null
                            : { tone: missingTone(holdsNextStep), label: t('detail.badges.notConnected') }
                    }
                />
                <OverviewRow
                    icon={KnowledgeIcon}
                    label={t('detail.knowledge.label')}
                    value={hasKnowledge ? knowledge.join(', ') : t('detail.knowledge.none')}
                    badge={
                        hasKnowledge ? null : { tone: missingTone(holdsNextStep), label: t('detail.badges.noDataYet') }
                    }
                />
            </div>

            <div className="flex flex-wrap gap-2">
                <Button asChild variant={nextStep === 'addKnowledge' ? 'primary' : 'outline'}>
                    <Link to={tabUrl('knowledge')}>
                        {hasKnowledge ? t('detail.knowledge.edit') : t('detail.knowledge.add')}
                    </Link>
                </Button>
                <Button asChild variant={nextStep === 'addIntegration' ? 'primary' : 'outline'}>
                    <Link to={tabUrl('integrations')}>
                        {hasIntegrations ? t('detail.integrations.edit') : t('detail.integrations.add')}
                    </Link>
                </Button>
            </div>
        </SettingsCard>
    );
};

const StatusCard = ({ agent, onChange }: { agent: any; onChange: () => Promise<unknown> }) => {
    const { t } = useTranslation('assistants');
    const { publishToast } = useToaster();
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

    const [setAgentEnabled, { loading: enabledLoading }] = useMutation(SET_AGENT_ENABLED);
    const navigate = useNavigate();
    const [deleteAgent, { loading: deleteLoading }] = useMutation(DELETE_AGENT, {
        refetchQueries: [{ query: GET_AGENTS }, 'GetNavigationAgents'],
    });
    const busy = enabledLoading || deleteLoading;

    const setEnabled = async (enabled: boolean) => {
        try {
            await setAgentEnabled({ variables: { id: agent.id, enabled } });
            await onChange();
            publishToast({
                description: t(enabled ? 'overview.resumeSuccess' : 'overview.pauseSuccess'),
                status: 'success',
            });
        } catch (error) {
            console.error(error);
            publishToast({ description: t(enabled ? 'overview.resumeError' : 'overview.pauseError'), status: 'error' });
        }
    };

    const deleteAssistant = async () => {
        try {
            await deleteAgent({ variables: { id: agent.id } });
            publishToast({ description: t('detail.status.deleteSuccess'), status: 'success' });
            navigate(url(ASSISTANTS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.status.deleteError'), status: 'error' });
            setIsConfirmingDelete(false);
        }
    };

    return (
        <SettingsCard
            title={t('detail.status.title')}
            description={agent.enabled ? t('detail.status.activeDescription') : t('detail.status.pausedDescription')}
        >
            <div className="flex flex-wrap gap-2">
                {agent.enabled ? (
                    <Button
                        type="button"
                        className="!border-line-warning-default !text-content-warning-strong"
                        disabled={busy}
                        onClick={() => setEnabled(false)}
                    >
                        {t('detail.status.pause')}
                    </Button>
                ) : (
                    <Button type="button" disabled={busy} onClick={() => setEnabled(true)}>
                        {t('detail.status.resume')}
                    </Button>
                )}
                <Button
                    type="button"
                    className="!border-line-danger-default !text-content-danger-strong"
                    disabled={busy}
                    onClick={() => setIsConfirmingDelete(true)}
                >
                    {t('detail.status.delete')}
                </Button>
            </div>

            {isConfirmingDelete && (
                <ConfirmModal
                    title={t('detail.status.deleteConfirmTitle')}
                    labelConfirm={t('detail.status.delete')}
                    onConfirm={deleteAssistant}
                    onCancel={() => setIsConfirmingDelete(false)}
                    confirmDisabled={busy}
                    danger
                >
                    <p className="text-sm">{t('detail.status.deleteConfirmMessage', { name: agent.name })}</p>
                </ConfirmModal>
            )}
        </SettingsCard>
    );
};

export default AssistantOverview;
